import { createAsyncThunk } from "@reduxjs/toolkit";

import { handleAPIErrorToast } from "@/component/toasts";
import {
  createScheduleVisitAsync,
  deleteManyScheduleVisitAsync,
  deleteScheduleVisitAsync,
  getAllScheduleVisitsAsync,
  getScheduleVisitsByOriginAsync,
  updateMultipleScheduleVisitAsync,
  updateScheduleVisitAsync,
} from "@/services/service-users/schedule-visit";
import { getZonesAsync } from "@/services/zones";

export const getZoneOptionsAsync = createAsyncThunk(
  "rosterReducer/getZoneOptions",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getZonesAsync();
      return response.data;
    } catch (error: any) {
      handleAPIErrorToast(error);
      return rejectWithValue(error);
    }
  }
);

export const getScheduleVisitsAsync = createAsyncThunk(
  "rosterReducer/getScheduleVisits",
  async (
    {
      startDate,
      endDate,
      filterInactivityVisits,
    }: { startDate: string; endDate: string; filterInactivityVisits?: boolean },
    { signal, rejectWithValue }
  ) => {
    try {
      const response = await getAllScheduleVisitsAsync(
        { startDate, endDate, filterInactivityVisits },
        signal
      );
      return response.data;
    } catch (error: any) {
      // ⛔ Ignore cancellation
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return rejectWithValue("cancelled");
      }

      return rejectWithValue(error);
    }
  }
);

export const getVisitsByOriginAsync = createAsyncThunk(
  "rosterReducer/getScheduleVisitsByOrigin",
  async (
    {
      id,
      startDate,
      endDate,
    }: { id: string; startDate?: string; endDate?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await getScheduleVisitsByOriginAsync({
        id,
        startDate,
        endDate,
      });
      return response.data;
    } catch (error: any) {
      // Ignore cancellation
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return rejectWithValue("cancelled");
      }

      return rejectWithValue(error);
    }
  }
);

export const createVisitAsync = createAsyncThunk(
  "rosterReducer/createVisit",
  async (data: Record<string, unknown>, { rejectWithValue }) => {
    try {
      const response = await createScheduleVisitAsync(data);
      return response.data;
    } catch (error: any) {
      if (error?.response?.status !== 409) {
        handleAPIErrorToast(error);
      }
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const updateVisitAsync = createAsyncThunk(
  "rosterReducer/updateVisit",
  async (
    { id, data }: { id: string; data: Record<string, unknown> },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateScheduleVisitAsync(id, data);
      return response.data;
    } catch (error: any) {
      if (error?.response?.status !== 409) {
        handleAPIErrorToast(error);
      }
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const updateMultipleVisitsAsync = createAsyncThunk(
  "rosterReducer/multipleUpdateVisit",
  async (
    {
      ids,
      updateData,
    }: {
      ids: string[];
      updateData: Record<string, unknown> | Record<string, unknown>[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateMultipleScheduleVisitAsync({
        ids,
        updateData,
      });
      return response.data;
    } catch (error: any) {
      if (error?.response?.status !== 409) {
        handleAPIErrorToast(error);
      }
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const deleteVisitAsync = createAsyncThunk(
  "rosterReducer/deleteVisit",
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteScheduleVisitAsync(id);
      return id;
    } catch (error: any) {
      rejectWithValue(error);
      handleAPIErrorToast(error);
      throw new Error("Failed to delete schedule visit");
    }
  }
);

export const deleteManyVisitAsync = createAsyncThunk(
  "rosterReducer/deleteManyVisit",
  async (data: { ids: string[] }, { rejectWithValue }) => {
    try {
      await deleteManyScheduleVisitAsync(data);
      return data;
    } catch (error: any) {
      rejectWithValue(error);
      handleAPIErrorToast(error);
      throw new Error("Failed to delete schedule visit");
    }
  }
);
